import { assertCents, distribute, sumCents } from "../money.js";
/**
 * A divisao de uma despesa. Esta funcao e o coracao do sistema: era o que as
 * views SQL do prototipo faziam (valor / numero de devedores + o "helper"),
 * agora com pesos, itens e arredondamento honesto de centavos.
 */
export class SplitError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.name = "SplitError";
        this.code = code;
    }
}
function validateBase(input) {
    assertCents(input.amountCents, "valor da despesa");
    if (input.amountCents <= 0) {
        throw new SplitError("AMOUNT_NOT_POSITIVE", "O valor da despesa precisa ser maior que zero.");
    }
    if (input.shares.length === 0) {
        throw new SplitError("NO_SHARES", "Selecione pelo menos uma pessoa para dividir a despesa.");
    }
    const seen = new Set();
    for (const share of input.shares) {
        if (seen.has(share.personId)) {
            throw new SplitError("DUPLICATED_SHARE", "A mesma pessoa foi incluída duas vezes na divisão.");
        }
        seen.add(share.personId);
    }
}
/** Peso valido: numero finito e nao negativo. Ausente vira 1. */
function weightOf(weight) {
    if (weight === undefined)
        return 1;
    if (!Number.isFinite(weight) || weight < 0) {
        throw new SplitError("INVALID_WEIGHT", "Os pesos precisam ser números maiores ou iguais a zero.");
    }
    return weight;
}
function splitEqual(input) {
    const ids = input.shares.map((share) => share.personId);
    const parts = distribute(input.amountCents, ids.map(() => 1));
    return new Map(ids.map((id, index) => [id, parts[index]]));
}
function splitByWeight(input) {
    const weights = input.shares.map((share) => weightOf(share.weight));
    if (sumCents(weights) <= 0) {
        throw new SplitError("ZERO_WEIGHT", "A soma dos pesos precisa ser maior que zero.");
    }
    const parts = distribute(input.amountCents, weights);
    return new Map(input.shares.map((share, index) => [share.personId, parts[index]]));
}
function splitExact(input) {
    const result = new Map();
    for (const share of input.shares) {
        const value = share.exactAmountCents;
        if (value === undefined || value === null) {
            throw new SplitError("MISSING_EXACT_AMOUNT", "No modo de valor exato, todas as pessoas precisam ter um valor informado.");
        }
        assertCents(value, "valor exato");
        if (value < 0) {
            throw new SplitError("NEGATIVE_EXACT_AMOUNT", "Os valores exatos não podem ser negativos.");
        }
        result.set(share.personId, value);
    }
    const total = sumCents([...result.values()]);
    if (total !== input.amountCents) {
        throw new SplitError("EXACT_SUM_MISMATCH", "A soma dos valores exatos precisa ser igual ao valor total da despesa.");
    }
    return result;
}
function splitByItems(input) {
    const items = input.items ?? [];
    if (items.length === 0) {
        throw new SplitError("NO_ITEMS", "Adicione pelo menos um item para dividir por consumo.");
    }
    const participants = new Set(input.shares.map((share) => share.personId));
    const result = new Map(input.shares.map((share) => [share.personId, 0]));
    for (const item of items) {
        assertCents(item.amountCents, "valor do item");
        if (item.amountCents < 0) {
            throw new SplitError("NEGATIVE_ITEM", "O valor de um item não pode ser negativo.");
        }
        if (item.shares.length === 0) {
            throw new SplitError("ITEM_WITHOUT_PEOPLE", `Marque quem consumiu o item "${item.name ?? "sem nome"}".`);
        }
        for (const itemShare of item.shares) {
            if (!participants.has(itemShare.personId)) {
                throw new SplitError("ITEM_PERSON_NOT_IN_EXPENSE", "Um item foi marcado para alguém que não está na divisão da despesa.");
            }
        }
        const parts = distribute(item.amountCents, item.shares.map((itemShare) => weightOf(itemShare.weight)));
        item.shares.forEach((itemShare, index) => {
            result.set(itemShare.personId, (result.get(itemShare.personId) ?? 0) + parts[index]);
        });
    }
    const itemsTotal = sumCents(items.map((item) => item.amountCents));
    const leftover = input.amountCents - itemsTotal;
    if (leftover < 0) {
        throw new SplitError("ITEMS_EXCEED_TOTAL", "A soma dos itens ficou maior que o valor total da despesa.");
    }
    if (leftover > 0) {
        // A sobra e a taxa de servico, o couvert, a entrega: nao pertence a item nenhum.
        const ids = input.shares.map((share) => share.personId);
        const proportional = ids.map((id) => result.get(id) ?? 0);
        const useProportional = (input.leftoverMode ?? "PROPORTIONAL") === "PROPORTIONAL" && sumCents(proportional) > 0;
        const weights = useProportional ? proportional : ids.map(() => 1);
        const parts = distribute(leftover, weights);
        ids.forEach((id, index) => {
            result.set(id, (result.get(id) ?? 0) + parts[index]);
        });
    }
    return result;
}
/**
 * Aplica o modo de divisao escolhido e, no fim, transfere a parte de quem tem
 * um "helper" para o helper. Devolve sempre uma linha por participante, com a
 * garantia de que a soma dos valores e exatamente o total da despesa.
 */
export function splitExpense(input) {
    validateBase(input);
    let amounts;
    switch (input.splitMode) {
        case "EQUAL":
            amounts = splitEqual(input);
            break;
        case "WEIGHT":
            amounts = splitByWeight(input);
            break;
        case "EXACT":
            amounts = splitExact(input);
            break;
        case "ITEMS":
            amounts = splitByItems(input);
            break;
        default:
            throw new SplitError("UNKNOWN_SPLIT_MODE", "Modo de divisão desconhecido.");
    }
    const computed = input.shares.map((share) => {
        const covered = share.coveredByPersonId;
        // Bancar a propria parte nao e bancar nada: o vinculo e ignorado.
        const chargedToPersonId = covered && covered !== share.personId ? covered : share.personId;
        return {
            personId: share.personId,
            amountCents: amounts.get(share.personId) ?? 0,
            chargedToPersonId,
        };
    });
    const total = sumCents(computed.map((share) => share.amountCents));
    if (total !== input.amountCents) {
        // Rede de seguranca: se cair aqui, ha bug de arredondamento e e melhor
        // falhar alto do que gravar uma despesa que nao fecha.
        throw new SplitError("SPLIT_SUM_MISMATCH", "A soma das partes não fechou com o valor da despesa.");
    }
    return computed;
}
/** Preview usado pelo formulario: quanto cada um paga, ja com o helper aplicado. */
export function summarizeSplit(shares) {
    const byPerson = new Map();
    for (const share of shares) {
        byPerson.set(share.chargedToPersonId, (byPerson.get(share.chargedToPersonId) ?? 0) + share.amountCents);
    }
    return byPerson;
}
//# sourceMappingURL=split.js.map